import React, { useMemo } from 'react';
import { useModals } from '@sls-react/contexts/ModalsContext';
import { useMutation, useQueryClient } from 'react-query';
import { UseMutationOptions } from 'react-query/types/react/types';
import {
  RailsItem,
  RailsItemAction,
  RailsThumbnailItem,
} from '@sls-react/typings/services/rails/item';
import {
  addItemToSale,
  deleteDiscardDraft,
  itemRemoveGmc,
  postPublishItem,
  postTakeDownItem,
  removeItemFromSale,
  unreportItem,
} from '@sls-react/api/item';
import {
  BumpIcon,
  DuplicateIcon,
  EditIcon,
  PriceDropIcon,
  PublishIcon,
  RemoveIcon,
  SaleTagIcon,
  StatsIcon,
  SwapsIcon,
  UnreportIcon,
} from '@sls-react/static/svg/ItemActionIcons';
import SwapsInboxLink from '@sls-react/components/Links/SwapsInboxLink';
import ListFlowLink from '@sls-react/components/Links/ListFlowLink';
import { RailsFacetItemMeta } from '@sls-react/typings/services/rails/facets';
import useBumpItem from '@sls-react/hooks/useBumpItem';
import ErrorMessage from '@sls-react/components/Form/ErrorMessage';
import { useSale } from '@sls-react/contexts/SaleContext';
import { Message } from '@sls-react/design-system';
import ReportIcon from '@sls-react/static/svg/ReportIcon';
import { NegativeFeedbackIcon } from '@sls-react/static/svg/FeedbackIcons';
import useCopyImageToClipboard from '@sls-react/hooks/useCopyImageToClipboard';
import { ButtonLink } from '@sls-react/components/Form/Button';

const actionOrder: RailsItemAction[] = [
  'bump',
  'edit',
  'join_sale',
  'leave_sale',
  'price_drop',
  'publish',
  'view_swap',
  'view_swaps',
  'duplicate',
  'discard_draft',
  'remove',
  'take_down',
  'unreport',
  'remove_gmc',
];

interface Props {
  item: RailsThumbnailItem | RailsItem;
  meta?: RailsFacetItemMeta;
  onInvalidate?: () => void;
  onViewQualityScore?: () => void;
}

const ItemThumbnailOverlayActions: React.FC<Props> = ({
  item,
  meta,
  onInvalidate,
  onViewQualityScore,
}) => {
  const modals = useModals();
  const queryClient = useQueryClient();
  const buttonClasses =
    'rounded-sm w-full py-2 px-2 flex items-center text-white text-xs font-semibold text-left transition duration-200 hover:bg-grey-900 disabled:opacity-50';

  const { sale } = useSale();

  const hasJoinSale = meta?.actions.includes('join_sale');
  const hasTakeDown = meta?.actions.includes('take_down');

  const {
    onCopy: copyLargeUrl,
    success: imageCopiedToClipboard,
    error: errorCopyingImage,
    loading: isCopyingImage,
  } = useCopyImageToClipboard(
    item.primary_image?.large_url || item.images[0]?.large_url,
  );

  const {
    mutate: handleBump,
    isLoading: isBumping,
    error: bumpError,
  } = useBumpItem(item.id, onInvalidate);

  const mutationOptions: UseMutationOptions = {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        exact: false,
        predicate: query => query.queryHash.includes(String(item.id)),
      });
      await onInvalidate?.();
    },
  };

  const availableActions = useMemo(() => {
    if (!meta) {
      return [];
    }

    return actionOrder.filter(function (
      this: RailsItemAction[],
      action: RailsItemAction,
    ) {
      return this.includes(action);
    },
    meta?.actions);
  }, [meta?.actions]);

  const {
    error: takeDownError,
    status: takeDownStatus,
    mutate: takeDownItem,
  } = useMutation(() => postTakeDownItem(item.id), mutationOptions);

  const {
    status: publishStatus,
    mutate: publishItem,
    error: publishError,
  } = useMutation(() => postPublishItem(item.id), mutationOptions);

  const {
    mutate: unreport,
    isLoading: isUnreporting,
    error: unreportError,
    isSuccess: isUnreported,
  } = useMutation(() => unreportItem(item.id), mutationOptions);

  const {
    mutate: removeGmc,
    isLoading: isRemovingFromGmc,
    error: removeGmcError,
    isSuccess: isRemovedFromGmc,
  } = useMutation(() => itemRemoveGmc(item.id), mutationOptions);

  const { mutate: discardDraft } = useMutation(
    () => deleteDiscardDraft(item.id),
    mutationOptions,
  );

  const {
    mutate: toggleSale,
    error: saleToggleError,
    isLoading: isTogglingSale,
  } = useMutation(
    () =>
      hasJoinSale
        ? addItemToSale(item.id, sale?.id!)
        : removeItemFromSale(item.id, sale?.id!),
    {
      onSuccess: onInvalidate,
    },
  );

  const handleClickPriceDrop: React.MouseEventHandler = e => {
    e.preventDefault();

    modals.openModal('priceDrop', {
      item,
      onSuccessfulSubmit: onInvalidate,
    });
  };

  const error =
    publishError ||
    takeDownError ||
    bumpError ||
    saleToggleError ||
    unreportError ||
    errorCopyingImage;

  return (
    <>
      {error && <ErrorMessage errors={error} />}

      {isUnreported && <Message success>Item unreported</Message>}
      {isRemovedFromGmc && <Message success>Removed from GMC</Message>}
      {imageCopiedToClipboard && (
        <Message success>Copied image to clipboard</Message>
      )}
      {takeDownStatus === 'success' && (
        <Message success>Item has been taken down</Message>
      )}

      {hasTakeDown && meta?.quality_score && (
        <button
          key="quality-score"
          id="quality-score"
          className={buttonClasses}
          onClick={onViewQualityScore}
        >
          <StatsIcon height={16} width={16} className="mr-2" /> Quality Score
        </button>
      )}

      {availableActions.map(action => {
        switch (action) {
          case 'edit':
            return (
              <ListFlowLink
                key={action}
                id={action}
                itemId={item.id}
                className={buttonClasses}
              >
                <EditIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Edit
              </ListFlowLink>
            );

          case 'publish':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={() => publishItem()}
                disabled={publishStatus === 'loading'}
              >
                <PublishIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Publish
              </button>
            );

          case 'bump':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={() => handleBump()}
                disabled={isBumping}
              >
                <BumpIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Bump
              </button>
            );

          case 'view_swaps':
            return (
              <SwapsInboxLink page="item" itemId={item.id} className="block">
                <button key={action} id={action} className={buttonClasses}>
                  <SwapsIcon
                    height={16}
                    width={16}
                    fill="currentColor"
                    className="mr-2"
                  />{' '}
                  View Swaps
                </button>
              </SwapsInboxLink>
            );

          case 'duplicate':
            return (
              <ListFlowLink src="item" duplicateId={item.id} className="block">
                <button key={action} id={action} className={buttonClasses}>
                  <DuplicateIcon
                    height={16}
                    width={16}
                    fill="currentColor"
                    className="mr-2"
                  />{' '}
                  Duplicate
                </button>
              </ListFlowLink>
            );

          case 'remove':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={() =>
                  modals.openModal('removeReport', {
                    item,
                    onSuccessfulSubmit: onInvalidate,
                    type: 'removeItem',
                  })
                }
              >
                <RemoveIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Remove
              </button>
            );

          case 'discard_draft':
            return (
              <button
                key={action}
                id={action}
                className={`${buttonClasses} text-red-500`}
                onClick={() => discardDraft()}
              >
                <RemoveIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Discard Draft
              </button>
            );

          case 'take_down':
            // Kinda odd, but "take down" includes the "Copy Image" action
            return (
              <>
                <button
                  key={action}
                  id={action}
                  className={`${buttonClasses} text-red-500`}
                  onClick={() => takeDownItem()}
                  disabled={takeDownStatus === 'loading'}
                >
                  <RemoveIcon
                    height={16}
                    width={16}
                    fill="currentColor"
                    className="mr-2"
                  />{' '}
                  Take Down
                </button>

                <button
                  key="copy-image"
                  id="copy-image"
                  className={buttonClasses}
                  onClick={() => copyLargeUrl()}
                  disabled={isCopyingImage}
                >
                  <DuplicateIcon
                    height={16}
                    width={16}
                    fill="currentColor"
                    className="mr-2"
                  />{' '}
                  Copy Image
                </button>
              </>
            );

          case 'join_sale':
            return (
              <button
                key={action}
                id={action}
                className={`${buttonClasses} text-green-500`}
                onClick={() => toggleSale()}
                disabled={isTogglingSale}
              >
                <SaleTagIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Add to Sale
              </button>
            );

          case 'leave_sale':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={() => toggleSale()}
                disabled={isTogglingSale}
              >
                <SaleTagIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Remove from Sale
              </button>
            );

          case 'price_drop':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={handleClickPriceDrop}
              >
                <PriceDropIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Price Drop
              </button>
            );

          case 'unreport':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={() => unreport()}
                disabled={isUnreporting}
              >
                <UnreportIcon height={16} width={16} className="mr-2" />{' '}
                Unreport
              </button>
            );

          case 'remove_gmc':
            return (
              <button
                key={action}
                id={action}
                className={buttonClasses}
                onClick={() => removeGmc()}
                disabled={isRemovingFromGmc}
              >
                <NegativeFeedbackIcon
                  height={16}
                  width={16}
                  fill="currentColor"
                  className="mr-2"
                />{' '}
                Remove from GMC
              </button>
            );

          default:
            return null;
        }
      })}
    </>
  );
};

export default ItemThumbnailOverlayActions;
